import React from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { services, industries, companyInfo } from '../data/mock';
import { TrendingUp, Globe, Palette, Camera, Video, Brain, Megaphone, Phone, Briefcase, Target, ArrowRight, MessageCircle, Sparkles, CheckCircle } from 'lucide-react';

const iconComponents = {
  TrendingUp,
  Globe,
  Palette,
  Camera,
  Video,
  Brain,
  Megaphone,
  Phone,
  Briefcase,
  Target,
};

const ServicesPage = () => {
  return (
    <Layout>
      {/* Hero */}
      <section className="pt-32 pb-16 bg-gradient-to-b from-blue-50/50 to-white relative overflow-hidden">
        <div className="absolute inset-0 ai-grid-pattern" />
        <div className="absolute top-20 right-20 w-72 h-72 bg-blue-500/10 rounded-full blur-3xl" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <span className="inline-flex items-center px-3 py-1 rounded-full bg-blue-50 text-blue-600 text-sm font-medium mb-4">
              <Sparkles className="w-4 h-4 mr-2" />
              Our Services
            </span>
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
              360° Business Promotion & <span className="text-blue-600">Growth Solutions</span>
            </h1>
            <p className="text-gray-600 text-lg leading-relaxed">
              Comprehensive AI-powered services to build, grow, and scale your business with modern technology and creative strategies.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => {
              const Icon = iconComponents[service.icon] || Target;
              const isAI = service.title.includes('AI');
              return (
                <div
                  key={service.id}
                  className={`group p-8 bg-white border rounded-2xl card-hover ${
                    isAI 
                      ? 'border-blue-200 bg-gradient-to-br from-blue-50/50 to-white shadow-xl shadow-blue-100/50' 
                      : 'border-gray-100 hover:border-blue-200 shadow-lg shadow-gray-100/50'
                  }`}
                >
                  <div className="flex items-start space-x-5">
                    <div className={`w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 ${
                      isAI 
                        ? 'bg-blue-600 text-white' 
                        : 'bg-blue-50 text-blue-600 group-hover:bg-blue-600 group-hover:text-white'
                    } transition-colors`}>
                      <Icon className="w-7 h-7" />
                    </div>
                    <div>
                      {isAI && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-blue-600 text-white text-xs font-medium mb-2">
                          AI-Powered
                        </span>
                      )}
                      <h3 className="text-xl font-semibold text-gray-900 mb-3">
                        {service.title}
                      </h3>
                      <p className="text-gray-600 text-sm leading-relaxed">
                        {service.fullDesc}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="py-24 bg-gradient-to-b from-white to-blue-50/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-flex items-center px-3 py-1 rounded-full bg-blue-50 text-blue-600 text-sm font-medium mb-4">
              Industries
            </span>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Industries We <span className="text-blue-600">Serve</span></h2>
            <p className="text-gray-600">Tailored solutions for every business sector</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {industries.map((industry, index) => (
              <div
                key={index}
                className="flex items-center gap-3 px-5 py-4 bg-white border border-gray-100 rounded-xl hover:bg-blue-50 hover:border-blue-200 transition-all cursor-default group shadow-sm"
              >
                <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0" />
                <span className="text-gray-700 text-sm font-medium group-hover:text-blue-700">
                  {industry}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-gray-900 relative overflow-hidden">
        <div className="absolute inset-0 ai-grid-pattern opacity-10" />
        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-6">
              Ready to Get Started?
            </h2>
            <p className="text-gray-400 mb-8">
              Contact us today to discuss your project and discover how our AI-powered solutions can help your business grow.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/quote">
                <Button
                  size="lg"
                  className="bg-blue-600 text-white hover:bg-blue-700 px-8 py-6 font-semibold shadow-lg shadow-blue-600/25 group"
                >
                  Get a Quote
                  <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
                </Button>
              </Link>
              <a href={companyInfo.whatsappLink} target="_blank" rel="noopener noreferrer">
                <Button
                  size="lg"
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white/10 px-8 py-6 font-semibold"
                >
                  <MessageCircle className="mr-2 w-4 h-4" />
                  Talk to Us
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ServicesPage;
